/**
 * Crear o editar un producto. Layout y lógica espejan al desktop
 * (`apps/desktop/src/renderer/src/pages/ProductEdit.tsx`) para que el
 * operador tenga el mismo flujo en web y desktop:
 *   - Nombre auto-deriva SKU (editable).
 *   - Precio de compra + precio de venta lado a lado con preview de
 *     utilidad/margen en vivo.
 *   - IVA viene de `tenant.taxRates`; se default-selecciona el primero
 *     al crear.
 *
 * No incluido vs desktop: upload de imagen (requiere endpoint R2 que
 * aún no está porteado; el campo se mantiene pero oculto).
 */

import { Button, Card, CardContent } from '@atreus/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';

import { ImageDropzone } from '../components/ImageDropzone.js';
import { formatCOP, parseCOPToCents } from '../lib/format.js';
import { buildSku } from '../lib/sku.js';
import { trpc } from '../lib/trpc.js';

interface Props {
  mode: 'create' | 'edit';
  productId?: string;
}

interface FormState {
  name: string;
  imageUrl: string | null;
  sku: string;
  description: string;
  priceCOP: string;
  /** Costo / precio de compra en string (el usuario escribe en COP).
   * Vacío equivale a "no registrado" → se manda como `null`. */
  costCOP: string;
  categoryId: string;
  taxRateId: string;
  active: boolean;
}

const emptyForm: FormState = {
  name: '',
  imageUrl: null,
  sku: '',
  description: '',
  priceCOP: '',
  costCOP: '',
  categoryId: '',
  taxRateId: '',
  active: true,
};

export const ProductEditPage = ({ mode, productId }: Props) => {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const categories = trpc.categories.list.useQuery();
  const taxes = trpc.tenant.taxRates.useQuery();
  const existing = trpc.products.get.useQuery(
    { id: productId ?? '' },
    { enabled: mode === 'edit' && !!productId },
  );

  const [form, setForm] = useState<FormState>(emptyForm);
  const [skuEditedByUser, setSkuEditedByUser] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === 'edit' && existing.data) {
      setForm({
        name: existing.data.name,
        imageUrl: existing.data.imageUrl,
        sku: existing.data.sku,
        description: existing.data.description ?? '',
        priceCOP: String(existing.data.priceCents / 100),
        costCOP:
          existing.data.costCents != null
            ? String(existing.data.costCents / 100)
            : '',
        categoryId: existing.data.categoryId ?? '',
        taxRateId: existing.data.taxRateId ?? '',
        active: existing.data.active,
      });
      setSkuEditedByUser(true);
    }
  }, [mode, existing.data]);

  // Default-selecciona la primera tasa IVA al crear.
  useEffect(() => {
    const first = taxes.data?.[0];
    if (mode === 'create' && form.taxRateId === '' && first) {
      setForm((f) => ({ ...f, taxRateId: first.id }));
    }
  }, [mode, taxes.data, form.taxRateId]);

  const handleNameChange = (name: string) => {
    setForm((f) => ({
      ...f,
      name,
      sku: skuEditedByUser ? f.sku : buildSku(name),
    }));
  };

  const handleSkuChange = (sku: string) => {
    const normalized = buildSku(sku);
    setForm((f) => ({ ...f, sku: normalized }));
    setSkuEditedByUser(normalized.length > 0);
  };

  const create = trpc.products.create.useMutation({
    onSuccess: () => {
      void utils.products.list.invalidate();
      void navigate({ to: '/products' });
    },
    onError: (e) => setError(e.message),
  });
  const update = trpc.products.update.useMutation({
    onSuccess: () => {
      void utils.products.list.invalidate();
      if (productId) void utils.products.get.invalidate({ id: productId });
      void navigate({ to: '/products' });
    },
    onError: (e) => setError(e.message),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const priceCents = parseCOPToCents(form.priceCOP);
    const costCents =
      form.costCOP.trim() === '' ? null : parseCOPToCents(form.costCOP);
    const payload = {
      sku: form.sku.trim(),
      name: form.name.trim(),
      description: form.description.trim() || null,
      priceCents,
      costCents,
      categoryId: form.categoryId || null,
      taxRateId: form.taxRateId || null,
      imageUrl: form.imageUrl,
      active: form.active,
    };
    if (mode === 'create') {
      create.mutate(payload);
    } else if (productId) {
      update.mutate({ id: productId, ...payload });
    }
  };

  const pending = create.isPending || update.isPending;

  // Utilidad + margen en vivo. Null si falta dato o si el costo es 0.
  const marginPreview = useMemo(() => {
    const price = parseCOPToCents(form.priceCOP);
    if (form.costCOP.trim() === '') return null;
    const cost = parseCOPToCents(form.costCOP);
    if (price <= 0 || cost < 0) return null;
    const profit = price - cost;
    const marginPct = price > 0 ? (profit / price) * 100 : 0;
    return { profit, marginPct };
  }, [form.priceCOP, form.costCOP]);

  if (mode === 'edit' && existing.isLoading) {
    return <div className="p-8 text-fg-muted">Cargando…</div>;
  }

  return (
    <div className="mx-auto max-w-2xl p-8">
      <Button asChild variant="ghost" size="sm">
        <Link to="/products">
          <ArrowLeft className="size-4" /> Volver
        </Link>
      </Button>

      <header className="mb-6 mt-4">
        <h1 className="text-3xl font-black tracking-tight">
          {mode === 'create' ? 'Nuevo producto' : 'Editar producto'}
        </h1>
      </header>

      <Card>
        <CardContent className="p-6">
          <form onSubmit={submit} className="space-y-5" autoComplete="off">
            <Field label="Nombre *">
              {(id) => (
                <input
                  id={id}
                  required
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className={inputCls}
                  placeholder="Ej: Hamburguesa clásica"
                  maxLength={200}
                  autoFocus
                />
              )}
            </Field>

            <Field
              label="Imagen"
              hint="Aparece en la grilla del POS y en el ticket."
            >
              <ImageDropzone
                value={form.imageUrl}
                onChange={(url) => setForm({ ...form, imageUrl: url })}
              />
            </Field>

            <Field
              label="SKU *"
              hint="Se genera automáticamente desde el nombre. Editable."
            >
              {(id) => (
                <input
                  id={id}
                  required
                  value={form.sku}
                  onChange={(e) => handleSkuChange(e.target.value)}
                  className={`${inputCls} font-mono uppercase`}
                  placeholder="HAMBURGUESA-CLASICA"
                  maxLength={64}
                />
              )}
            </Field>

            <Field label="Descripción">
              {(id) => (
                <textarea
                  id={id}
                  rows={3}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  className={`${inputCls} resize-none`}
                  placeholder="Opcional"
                  maxLength={1000}
                />
              )}
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Precio de compra (COP)"
                hint="Opcional. Se usa para calcular utilidad por producto."
              >
                {(id) => (
                  <input
                    id={id}
                    type="number"
                    min="0"
                    step="1"
                    value={form.costCOP}
                    onChange={(e) =>
                      setForm({ ...form, costCOP: e.target.value })
                    }
                    className={inputCls}
                    placeholder="8000"
                  />
                )}
              </Field>

              <Field label="Precio de venta (COP) *">
                {(id) => (
                  <input
                    id={id}
                    required
                    type="number"
                    min="0"
                    step="1"
                    value={form.priceCOP}
                    onChange={(e) =>
                      setForm({ ...form, priceCOP: e.target.value })
                    }
                    className={inputCls}
                    placeholder="15000"
                  />
                )}
              </Field>
            </div>

            {marginPreview ? (
              <div className="rounded-md border border-border bg-surface-muted px-3 py-2 text-sm">
                Utilidad por unidad:{' '}
                <strong
                  className={
                    marginPreview.profit < 0 ? 'text-danger' : 'text-fg'
                  }
                >
                  {formatCOP(marginPreview.profit)}
                </strong>{' '}
                <span className="text-fg-muted">
                  ({marginPreview.marginPct.toFixed(1)}% margen)
                </span>
              </div>
            ) : null}

            <Field label="IVA">
              {(id) => (
                <select
                  id={id}
                  value={form.taxRateId}
                  onChange={(e) =>
                    setForm({ ...form, taxRateId: e.target.value })
                  }
                  className={inputCls}
                >
                  <option value="">Sin IVA</option>
                  {taxes.data?.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            <Field label="Categoría">
              {(id) => (
                <select
                  id={id}
                  value={form.categoryId}
                  onChange={(e) =>
                    setForm({ ...form, categoryId: e.target.value })
                  }
                  className={inputCls}
                >
                  <option value="">Sin categoría</option>
                  {categories.data?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) =>
                  setForm({ ...form, active: e.target.checked })
                }
                className="size-4 accent-fg"
              />
              Activo (visible en grilla del POS)
            </label>

            {error ? (
              <div className="rounded border border-danger bg-danger/5 p-3 text-sm text-danger">
                {error}
              </div>
            ) : null}

            <div className="flex justify-end gap-2 pt-2">
              <Button asChild type="button" variant="outline">
                <Link to="/products">Cancelar</Link>
              </Button>
              <Button type="submit" disabled={pending}>
                <Save className="size-4" />
                {pending ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {mode === 'edit' && productId ? (
        <ModifiersSection productId={productId} />
      ) : null}
    </div>
  );
};

// ---------- Modifiers (sección editable) ----------

interface ModifierRow {
  id: string;
  name: string;
  priceDeltaCents: number;
  required: boolean;
}

interface ModifierDraft {
  id?: string;
  name: string;
  priceDeltaCOP: string; // string en COP mientras se edita
  required: boolean;
}

const emptyModifierDraft: ModifierDraft = {
  name: '',
  priceDeltaCOP: '',
  required: false,
};

const ModifiersSection = ({ productId }: { productId: string }) => {
  const utils = trpc.useUtils();
  const list = trpc.modifiers.listForProduct.useQuery({ productId });
  const invalidate = (): void => {
    void utils.modifiers.listForProduct.invalidate({ productId });
  };
  const create = trpc.modifiers.create.useMutation({ onSuccess: invalidate });
  const update = trpc.modifiers.update.useMutation({ onSuccess: invalidate });
  const del = trpc.modifiers.delete.useMutation({ onSuccess: invalidate });

  const [draft, setDraft] = useState<ModifierDraft | null>(null);

  const save = (): void => {
    if (!draft) return;
    const priceDeltaCents = draft.priceDeltaCOP.trim() === ''
      ? 0
      : parseCOPToCents(draft.priceDeltaCOP);
    if (draft.id) {
      update.mutate(
        {
          id: draft.id,
          name: draft.name.trim(),
          priceDeltaCents,
          required: draft.required,
        },
        { onSuccess: () => setDraft(null) },
      );
    } else {
      create.mutate(
        {
          productId,
          name: draft.name.trim(),
          priceDeltaCents,
          required: draft.required,
        },
        { onSuccess: () => setDraft(null) },
      );
    }
  };

  const handleDelete = (id: string, name: string): void => {
    if (!window.confirm(`¿Eliminar el modificador "${name}"?`)) return;
    del.mutate({ id });
  };

  const rows = (list.data ?? []) as ModifierRow[];
  const mutating = create.isPending || update.isPending || del.isPending;

  return (
    <Card className="mt-6">
      <CardContent className="p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold tracking-tight">Modificadores</h2>
            <p className="mt-0.5 text-xs text-fg-muted">
              Opciones que el cajero puede elegir al agregar este producto al
              pedido (ej. "Grande +$3.000", "Sin cebolla").
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => setDraft({ ...emptyModifierDraft })}
            disabled={draft !== null}
          >
            <Plus className="size-4" /> Agregar
          </Button>
        </div>

        {list.isLoading ? (
          <p className="text-sm text-fg-muted">Cargando…</p>
        ) : rows.length === 0 && draft === null ? (
          <p className="text-sm italic text-fg-muted">
            Sin modificadores configurados.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((m) => (
              <li
                key={m.id}
                className="flex items-center justify-between gap-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">
                    {m.name}
                    {m.required ? (
                      <span className="ml-2 rounded border border-border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-fg-muted">
                        Obligatorio
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-fg-muted tabular-nums">
                    {m.priceDeltaCents === 0
                      ? 'Sin costo extra'
                      : `${m.priceDeltaCents > 0 ? '+' : ''}${formatCOP(m.priceDeltaCents)}`}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setDraft({
                        id: m.id,
                        name: m.name,
                        priceDeltaCOP: String(m.priceDeltaCents / 100),
                        required: m.required,
                      })
                    }
                    disabled={mutating || draft !== null}
                    title="Editar"
                  >
                    <Pencil className="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(m.id, m.name)}
                    disabled={mutating}
                    title="Eliminar"
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {(create.error || update.error || del.error) ? (
          <div className="mt-3 rounded border border-danger bg-danger/5 p-2 text-sm text-danger">
            {create.error?.message ??
              update.error?.message ??
              del.error?.message}
          </div>
        ) : null}
      </CardContent>

      {draft !== null ? (
        <ModifierDialog
          draft={draft}
          onChange={setDraft}
          onSave={save}
          onClose={() => setDraft(null)}
          pending={create.isPending || update.isPending}
        />
      ) : null}
    </Card>
  );
};

const ModifierDialog = ({
  draft,
  onChange,
  onSave,
  onClose,
  pending,
}: {
  draft: ModifierDraft;
  onChange: (d: ModifierDraft) => void;
  onSave: () => void;
  onClose: () => void;
  pending: boolean;
}) => {
  const canSave = draft.name.trim().length > 0 && !pending;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface-raised shadow-xl">
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="text-lg font-black tracking-tight">
            {draft.id ? 'Editar modificador' : 'Nuevo modificador'}
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Cerrar">
            <X className="size-4" />
          </Button>
        </header>
        <form
          className="space-y-4 p-5"
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) onSave();
          }}
        >
          <div>
            <label className="mb-1 block text-sm font-semibold">Nombre</label>
            <input
              autoFocus
              value={draft.name}
              onChange={(e) => onChange({ ...draft, name: e.target.value })}
              placeholder="Grande, Sin cebolla, Extra queso…"
              className={inputCls}
              maxLength={100}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold">
              Costo adicional (COP)
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={draft.priceDeltaCOP}
              onChange={(e) =>
                onChange({ ...draft, priceDeltaCOP: e.target.value })
              }
              placeholder="0 para sin costo; usa negativos para descuentos"
              className={inputCls}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.required}
              onChange={(e) =>
                onChange({ ...draft, required: e.target.checked })
              }
              className="size-4 accent-fg"
            />
            Obligatorio (el cajero debe elegirlo al agregar)
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSave}>
              <Save className="size-4" />
              {pending ? 'Guardando…' : 'Guardar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const inputCls =
  'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none placeholder:text-fg-subtle focus:border-fg';

/**
 * Field wrapper — asocia `label` ↔ input via `htmlFor` cuando `children`
 * es una función que recibe el id auto-generado. Para composites sin un
 * único input (ej. dropzone), pasar `children` directo y queda sin `htmlFor`.
 */
const Field = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ((id: string) => ReactNode) | ReactNode;
}) => {
  const id = useId();
  const linked = typeof children === 'function';
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={linked ? id : undefined}
        className="block text-sm font-semibold tracking-tight"
      >
        {label}
      </label>
      {linked ? (children as (id: string) => ReactNode)(id) : children}
      {hint ? <div className="text-xs text-fg-subtle">{hint}</div> : null}
    </div>
  );
};
